/**
 * Attendance Templates Service
 * API calls for clinical attendance mask/template operations (anamnese/evolution)
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SymptomsBlock {
  predefined: string[];
  freeText?: string;
}

export interface DiseaseRef {
  cid?: string;
  internalCode?: string;
  description?: string;
}

export interface MedRef {
  name?: string;
  activeIngredient?: string;
  posologyBase?: string;
}

/**
 * Conteúdo estruturado de uma máscara de atendimento.
 * Cada campo corresponde a uma seção clínica da anamnese/evolução.
 */
export interface AttendanceTemplateContent {
  chiefComplaint?: string;   // Queixa principal
  hda?: string;              // História da Doença Atual
  symptoms?: SymptomsBlock;  // Sintomas (estruturado + livre)
  physicalExam?: string;     // Exame físico
  hypothesis?: string;       // Hipótese diagnóstica
  conduct?: string;          // Conduta médica
  observations?: string;     // Observações padrão
  // Extension points (futura integração)
  relatedDiseases?: DiseaseRef[];
  suggestedMedications?: MedRef[];
}

export interface AttendanceTemplateFeatureFlags {
  enableDiseaseSuggestions: boolean;
  enableMedicationSuggestions: boolean;
}

export interface AttendanceTemplateDTO {
  id: string;
  doctorId: string;
  name: string;
  category?: string | null;
  content: AttendanceTemplateContent;
  isActive: boolean;
  isFeatured: boolean;
  usageCount: number;
  featureFlags: AttendanceTemplateFeatureFlags;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAttendanceTemplateDTO {
  name: string;
  category?: string;
  content: AttendanceTemplateContent;
  isFeatured?: boolean;
}

export interface UpdateAttendanceTemplateDTO {
  name?: string;
  category?: string;
  content?: Partial<AttendanceTemplateContent>;
  isActive?: boolean;
  isFeatured?: boolean;
}

export interface ApplyAttendanceTemplateResult {
  templateId: string;
  templateName: string;
  content: AttendanceTemplateContent;
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Lista máscaras de atendimento do médico autenticado
 */
export async function listAttendanceTemplates(
  includeInactive = false,
): Promise<ApiResponse<AttendanceTemplateDTO[]>> {
  const url = includeInactive
    ? `${API_ENDPOINTS.ATTENDANCE_TEMPLATES.LIST}?includeInactive=true`
    : API_ENDPOINTS.ATTENDANCE_TEMPLATES.LIST;
  return apiClient.get(url);
}

/**
 * Busca uma máscara específica por ID
 */
export async function getAttendanceTemplate(
  id: string,
): Promise<ApiResponse<AttendanceTemplateDTO>> {
  return apiClient.get(API_ENDPOINTS.ATTENDANCE_TEMPLATES.GET(id));
}

/**
 * Cria uma nova máscara de atendimento
 */
export async function createAttendanceTemplate(
  data: CreateAttendanceTemplateDTO,
): Promise<ApiResponse<AttendanceTemplateDTO>> {
  return apiClient.post(API_ENDPOINTS.ATTENDANCE_TEMPLATES.CREATE, data);
}

/**
 * Atualiza uma máscara existente (PATCH parcial)
 */
export async function updateAttendanceTemplate(
  id: string,
  data: UpdateAttendanceTemplateDTO,
): Promise<ApiResponse<AttendanceTemplateDTO>> {
  return apiClient.patch(API_ENDPOINTS.ATTENDANCE_TEMPLATES.UPDATE(id), data);
}

/**
 * Remove (soft-delete) uma máscara
 */
export async function deleteAttendanceTemplate(
  id: string,
): Promise<ApiResponse<void>> {
  return apiClient.delete(API_ENDPOINTS.ATTENDANCE_TEMPLATES.DELETE(id));
}

/**
 * Duplica uma máscara existente
 */
export async function duplicateAttendanceTemplate(
  id: string,
): Promise<ApiResponse<AttendanceTemplateDTO>> {
  return apiClient.post(API_ENDPOINTS.ATTENDANCE_TEMPLATES.DUPLICATE(id), {});
}

/**
 * Aplica uma máscara — retorna conteúdo para pré-preenchimento do prontuário
 */
export async function applyAttendanceTemplate(
  id: string,
): Promise<ApiResponse<ApplyAttendanceTemplateResult>> {
  return apiClient.post(API_ENDPOINTS.ATTENDANCE_TEMPLATES.APPLY(id), {});
}
